import type { Generator } from "../sim/types.ts";

export interface ControlCallbacks {
  onSetpoint: (id: string, mw: number) => void;
  onToggle: (id: string) => void;
  showCost: boolean;
}

export class GeneratorPanel {
  private root: HTMLElement;
  private cards = new Map<string, HTMLElement>();

  constructor(container: HTMLElement, private cb: ControlCallbacks) {
    this.root = document.createElement("div");
    this.root.className = "gen-list";
    container.appendChild(this.root);
  }

  setGenerators(gens: Generator[]): void {
    this.root.innerHTML = "";
    this.cards.clear();
    for (const g of gens) {
      const card = this.makeCard(g);
      this.root.appendChild(card);
      this.cards.set(g.id, card);
    }
  }

  update(gens: Generator[]): void {
    for (const g of gens) {
      const card = this.cards.get(g.id);
      if (!card) continue;
      this.updateCard(card, g);
    }
  }

  private makeCard(g: Generator): HTMLElement {
    const card = document.createElement("div");
    card.className = "gen-card";
    card.dataset.id = g.id;

    const head = document.createElement("div");
    head.className = "head";
    const name = document.createElement("div");
    name.className = "name";
    name.textContent = g.name;
    const meta = document.createElement("div");
    meta.className = "meta";
    meta.textContent = labelType(g.type);
    const pill = document.createElement("div");
    pill.className = "status-pill";
    head.appendChild(name);
    head.appendChild(meta);
    head.appendChild(pill);
    card.appendChild(head);

    const outRow = document.createElement("div");
    outRow.className = "output-row";
    const mw = document.createElement("span");
    mw.className = "mw";
    const of = document.createElement("span");
    of.className = "of";
    outRow.appendChild(mw);
    outRow.appendChild(of);
    card.appendChild(outRow);

    const slider = document.createElement("input");
    slider.type = "range";
    const isBattery = g.type === "battery";
    slider.min = String(isBattery ? -g.pMax : g.pMin);
    slider.max = String(g.pMax);
    slider.step = "5";
    slider.value = String(isBattery ? g.setpoint : Math.max(g.pMin, g.setpoint));
    slider.addEventListener("input", () => {
      this.cb.onSetpoint(g.id, Number(slider.value));
    });
    card.appendChild(slider);
    if (isBattery) {
      const hint = document.createElement("div");
      hint.className = "info";
      hint.style.marginTop = "0";
      hint.innerHTML = `<span>charge ←</span><span>0</span><span>→ discharge</span>`;
      card.appendChild(hint);
    }

    const btnRow = document.createElement("div");
    btnRow.className = "row";
    const onBtn = document.createElement("button");
    onBtn.textContent = "On / Off";
    onBtn.addEventListener("click", () => this.cb.onToggle(g.id));
    btnRow.appendChild(onBtn);
    card.appendChild(btnRow);

    const info = document.createElement("div");
    info.className = "info";
    info.innerHTML = `
      <span>ramp ${g.rampRate} MW/min</span>
      ${this.cb.showCost ? `<span>$${g.cost}/MWh</span>` : ""}
    `;
    card.appendChild(info);

    this.updateCard(card, g);
    return card;
  }

  private updateCard(card: HTMLElement, g: Generator): void {
    const pill = card.querySelector(".status-pill")!;
    pill.className = `status-pill ${g.status}`;
    pill.textContent = g.status;

    const mw = card.querySelector(".output-row .mw")!;
    const of = card.querySelector(".output-row .of")!;
    mw.textContent = `${Math.round(g.output)} MW`;
    of.textContent = `setpoint ${Math.round(g.setpoint)} / max ${Math.round(g.pMax * g.availability)}`;

    const slider = card.querySelector("input[type=range]") as HTMLInputElement;
    slider.disabled = g.status === "off" || g.status === "tripped" || g.status === "stopping";
    // Update slider only when not actively dragged
    if (document.activeElement !== slider) {
      const isBattery = g.type === "battery";
      slider.value = String(isBattery ? g.setpoint : Math.max(g.pMin, g.setpoint));
    }
  }
}

function labelType(t: string): string {
  switch (t) {
    case "coal": return "Coal";
    case "nuclear": return "Nuclear";
    case "gas-ccgt": return "Gas CCGT";
    case "gas-peaker": return "Gas Peaker";
    case "hydro": return "Hydro";
    case "wind": return "Wind";
    case "solar": return "Solar";
    case "battery": return "Battery";
    default: return t;
  }
}
